import React, { useState } from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Brush, ReferenceLine } from 'recharts';

import Button from '../../../components/ui/Button';

const EquityCurveChart = ({ dateRange, selectedAccounts }) => {
  const [chartType, setChartType] = useState('equity'); // 'equity', 'drawdown', 'combined'
  const [selectedInstrument, setSelectedInstrument] = useState('all');

  // Mock equity curve data
  const equityData = [
    { date: '2024-01-01', equity: 100000, drawdown: 0, balance: 100000, timestamp: 1704067200000 },
    { date: '2024-01-15', equity: 102500, drawdown: -1.2, balance: 101200, timestamp: 1705276800000 },
    { date: '2024-02-01', equity: 105800, drawdown: -0.8, balance: 104800, timestamp: 1706745600000 },
    { date: '2024-02-15', equity: 103200, drawdown: -2.5, balance: 102500, timestamp: 1707955200000 },
    { date: '2024-03-01', equity: 108900, drawdown: -0.3, balance: 108600, timestamp: 1709251200000 },
    { date: '2024-03-15', equity: 112400, drawdown: -1.8, balance: 110600, timestamp: 1710460800000 },
    { date: '2024-04-01', equity: 115600, drawdown: -0.5, balance: 115100, timestamp: 1711929600000 },
    { date: '2024-04-15', equity: 118900, drawdown: -2.1, balance: 116800, timestamp: 1713139200000 },
    { date: '2024-05-01', equity: 122300, drawdown: -0.9, balance: 121400, timestamp: 1714521600000 },
    { date: '2024-05-15', equity: 125800, drawdown: -1.4, balance: 124400, timestamp: 1715731200000 },
    { date: '2024-06-01', equity: 128500, drawdown: -0.6, balance: 127900, timestamp: 1717200000000 },
    { date: '2024-06-15', equity: 132100, drawdown: -2.3, balance: 129800, timestamp: 1718409600000 },
    { date: '2024-07-01', equity: 135700, drawdown: -0.4, balance: 135300, timestamp: 1719792000000 },
    { date: '2024-07-15', equity: 139200, drawdown: -1.6, balance: 137600, timestamp: 1721001600000 },
    { date: '2024-08-01', equity: 142800, drawdown: -0.7, balance: 141800, timestamp: 1722470400000 },
    { date: '2024-08-15', equity: 146500, drawdown: -1.9, balance: 144600, timestamp: 1723680000000 },
    { date: '2024-09-01', equity: 150200, drawdown: -0.3, balance: 149900, timestamp: 1725148800000 },
    { date: '2024-09-15', equity: 153900, drawdown: -1.1, balance: 152800, timestamp: 1726358400000 },
    { date: '2024-10-01', equity: 157600, drawdown: -0.8, balance: 156800, timestamp: 1727740800000 },
    { date: '2024-10-06', equity: 160200, drawdown: -0.2, balance: 159800, timestamp: 1728172800000 }
  ];

  const instruments = [
    { value: 'all', label: 'All Instruments' },
    { value: 'EURUSD', label: 'EUR/USD' },
    { value: 'GBPUSD', label: 'GBP/USD' },
    { value: 'USDJPY', label: 'USD/JPY' },
    { value: 'AUDUSD', label: 'AUD/USD' },
    { value: 'USDCAD', label: 'USD/CAD' }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  const formatPercentage = (value) => {
    return `${value?.toFixed(1)}%`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">
            {new Date(label)?.toLocaleDateString()}
          </p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry?.color }}
              />
              <span className="text-muted-foreground">{entry?.name}:</span>
              <span className="font-medium text-foreground">
                {entry?.dataKey === 'drawdown' 
                  ? formatPercentage(entry?.value)
                  : formatCurrency(entry?.value)
                }
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    switch (chartType) {
      case 'equity':
        return (
          <LineChart data={equityData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(value) => new Date(value)?.toLocaleDateString()}
              stroke="var(--color-muted-foreground)"
            />
            <YAxis 
              tickFormatter={formatCurrency}
              stroke="var(--color-muted-foreground)"
            />
            <Tooltip content={<CustomTooltip />} />
            <Line 
              type="monotone" 
              dataKey="equity" 
              stroke="var(--color-primary)" 
              strokeWidth={2}
              dot={false}
              name="Equity"
            />
            <Line 
              type="monotone" 
              dataKey="balance" 
              stroke="var(--color-secondary)" 
              strokeWidth={1}
              strokeDasharray="5 5"
              dot={false}
              name="Balance"
            />
            <Brush 
              dataKey="timestamp" 
              height={30}
              stroke="var(--color-primary)"
              tickFormatter={(value) => new Date(value)?.toLocaleDateString()}
            />
          </LineChart>
        );

      case 'drawdown':
        return (
          <AreaChart data={equityData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(value) => new Date(value)?.toLocaleDateString()}
              stroke="var(--color-muted-foreground)"
            />
            <YAxis 
              tickFormatter={formatPercentage}
              stroke="var(--color-muted-foreground)"
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke="var(--color-border)" strokeDasharray="2 2" />
            <Area 
              type="monotone" 
              dataKey="drawdown" 
              stroke="var(--color-error)" 
              fill="var(--color-error)"
              fillOpacity={0.3}
              name="Drawdown"
            />
            <Brush 
              dataKey="timestamp" 
              height={30}
              stroke="var(--color-error)"
              tickFormatter={(value) => new Date(value)?.toLocaleDateString()}
            />
          </AreaChart>
        );

      case 'combined':
        return (
          <LineChart data={equityData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(value) => new Date(value)?.toLocaleDateString()}
              stroke="var(--color-muted-foreground)"
            />
            <YAxis 
              yAxisId="equity"
              orientation="left"
              tickFormatter={formatCurrency}
              stroke="var(--color-muted-foreground)"
            />
            <YAxis 
              yAxisId="drawdown"
              orientation="right"
              tickFormatter={formatPercentage}
              stroke="var(--color-muted-foreground)"
            />
            <Tooltip content={<CustomTooltip />} />
            <Line 
              yAxisId="equity"
              type="monotone" 
              dataKey="equity" 
              stroke="var(--color-primary)" 
              strokeWidth={2}
              dot={false}
              name="Equity"
            />
            <Line 
              yAxisId="drawdown"
              type="monotone" 
              dataKey="drawdown" 
              stroke="var(--color-error)" 
              strokeWidth={2}
              dot={false}
              name="Drawdown"
            />
            <Brush 
              dataKey="timestamp" 
              height={30}
              stroke="var(--color-primary)"
              tickFormatter={(value) => new Date(value)?.toLocaleDateString()}
            />
          </LineChart>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">
            Portfolio Performance
          </h3>
          <p className="text-sm text-muted-foreground">
            Equity curve and drawdown analysis over time
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-4 lg:mt-0">
          <select
            value={selectedInstrument}
            onChange={(e) => setSelectedInstrument(e?.target?.value)}
            className="px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {instruments?.map((instrument) => (
              <option key={instrument?.value} value={instrument?.value}>
                {instrument?.label}
              </option>
            ))}
          </select>

          <div className="flex bg-muted rounded-md p-1">
            <Button
              variant={chartType === 'equity' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setChartType('equity')}
              className="text-xs"
            >
              Equity
            </Button>
            <Button
              variant={chartType === 'drawdown' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setChartType('drawdown')}
              className="text-xs"
            >
              Drawdown
            </Button>
            <Button
              variant={chartType === 'combined' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setChartType('combined')}
              className="text-xs"
            >
              Combined
            </Button>
          </div>
        </div>
      </div>
      <div className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4 pt-6 border-t border-border">
        <div className="text-center">
          <div className="text-sm text-muted-foreground">Current Equity</div>
          <div className="text-lg font-semibold text-foreground">
            {formatCurrency(160200)}
          </div>
        </div>
        <div className="text-center">
          <div className="text-sm text-muted-foreground">Max Drawdown</div>
          <div className="text-lg font-semibold text-red-600">
            {formatPercentage(-2.5)}
          </div>
        </div>
        <div className="text-center">
          <div className="text-sm text-muted-foreground">Total Return</div>
          <div className="text-lg font-semibold text-green-600">
            {formatPercentage(60.2)}
          </div>
        </div>
        <div className="text-center">
          <div className="text-sm text-muted-foreground">Recovery Factor</div>
          <div className="text-lg font-semibold text-foreground">
            24.08
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquityCurveChart;